import { useState, useMemo, useEffect } from "react";
import { convertAllState, unitsForState, unitSqm } from "@/lib/stateUnits";
import { addHistory } from "@/lib/storage";
import { formatNumber } from "@/lib/units";
import ResultsPanel, { CalcPageHeader, Field } from "@/components/ResultsPanel";
import EduSection from "@/components/EduSection";
import AdSlot from "@/components/AdSlot";
import { useApp } from "@/lib/appContext";
import { useSeo } from "@/lib/seo";
import { stateName } from "@/lib/states";

export default function LandConverter() {
  const { state } = useApp();
  const units = useMemo(() => unitsForState(state), [state]);
  const [value, setValue] = useState("1");
  const [fromKey, setFromKey] = useState("bigha");

  // If current fromKey isn't valid for this state, reset to sqft
  useEffect(() => {
    if (!units.find((u) => u.key === fromKey)) setFromKey("sqft");
  }, [state, units, fromKey]);

  const results = useMemo(() => convertAllState(Number(value) || 0, fromKey, state), [value, fromKey, state]);

  useSeo({
    title: `Land Unit Converter — ${stateName(state)}`,
    description: `Convert Bigha, Katha, Gaj, Acre, Ground, Ankanam and every Indian land unit — accurate for ${stateName(state)}.`,
    path: "/land-converter",
  });

  useEffect(() => {
    const num = Number(value);
    if (!num) return;
    const t = setTimeout(() => {
      addHistory({ slug: "land-converter", title: "Land Unit Converter", inputs: { value: num, fromUnit: fromKey, state } });
    }, 1000);
    return () => clearTimeout(t);
  }, [value, fromKey, state]);

  const rows = units.map((u) => [`${u.label} (${u.short})`, formatNumber(results[u.key])]);
  const fromUnit = units.find((u) => u.key === fromKey);
  const bighaSqm = unitSqm("bigha", state).toFixed(2);

  return (
    <div>
      <CalcPageHeader title="Land Unit Converter" description={`State-aware conversion. 1 Bigha in ${stateName(state)} = ${bighaSqm} m².`} slug="land-converter" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7 space-y-6">
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
              <div className="sm:col-span-3">
                <Field label="Value">
                  <input data-testid="converter-value-input" type="number" inputMode="decimal" value={value} onChange={(e)=>setValue(e.target.value)}
                    className="w-full h-11 px-3 rounded-md border border-border bg-background text-lg font-mono outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" placeholder="Enter value" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="From unit">
                  <select data-testid="converter-from-unit" value={fromKey} onChange={(e)=>setFromKey(e.target.value)}
                    className="w-full h-11 px-3 rounded-md border border-border bg-background text-sm outline-none focus:ring-2 focus:ring-primary/40">
                    {units.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-5 sm:p-6">
            <div className="overline mb-3">Conversion grid · {stateName(state)}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="conversion-grid">
              {units.filter((u) => u.key !== fromKey).map((u) => (
                <div key={u.key} data-testid={`convert-row-${u.key}`} className="flex items-baseline justify-between gap-3 px-3 py-2.5 rounded-md hover:bg-accent/60 transition">
                  <span className="text-sm text-muted-foreground">{u.label}</span>
                  <span className="stat-num text-sm font-semibold">
                    {formatNumber(results[u.key])} <span className="text-[10px] text-muted-foreground uppercase ml-1">{u.short}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <AdSlot slot="converter-mid" testId="ad-converter-mid" />
        </div>

        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <ResultsPanel title="Land Unit Converter" slug="land-converter"
            highlight={{ label: `${value || 0} ${fromUnit?.short || ""} equals`, value: `${formatNumber(results.sqm)} m²`, sub: `${formatNumber(results.sqft)} sq ft · ${formatNumber(results.acre)} acre` }}
            rows={rows} onReset={() => { setValue("1"); setFromKey("bigha"); }}
            note={`State: ${stateName(state)}. Regional factors: 1 Bigha ≈ ${bighaSqm} m² here.`}
          />
        </div>
      </div>

      <EduSection
        howItWorks={<>
          <p><strong>Indian land units vary by state.</strong> A Bigha in Uttar Pradesh (≈ 2,529 m²) is completely different from a Bigha in West Bengal (≈ 1,338 m²) or Haryana/Punjab (≈ 4,047 m²). LandCalc automatically applies the correct factor for your selected state.</p>
          <p>All units are normalised via square metres (SI standard). We then convert to your target unit using the appropriate state factor. Change your state anytime from the pill in the header.</p>
        </>}
        faqs={[
          { q: "How many square feet is 1 Bigha in Uttar Pradesh?", a: "1 Bigha in UP ≈ 27,225 sq ft (2,529.28 m²). Set your state to UP and enter 1 Bigha to confirm." },
          { q: "How many Gaj is 1 Katha?", a: "1 Katha in UP/Bihar ≈ 151 Gaj (126.44 m² ÷ 0.8361 m²/Gaj). Values differ in West Bengal and Assam — LandCalc uses the correct regional factor." },
          { q: "Is 1 Acre equal to 1 Hectare?", a: "No. 1 Hectare = 2.471 Acres (10,000 m² vs 4,046.86 m²). Both are SI-based and do not change by state." },
          { q: "Which units apply to South India?", a: "Cent, Ground, Ankanam, Guntha and Are are commonly used in Tamil Nadu, Kerala, Andhra Pradesh, Karnataka and Telangana. Switch state to see the relevant grid." },
        ]}
        related={["plot","stamp-duty","far","construction"]}
      />
    </div>
  );
}
